import { type FC, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMeasure from 'react-use-measure';
import { useApplyChestMutation } from '@api/queries/contract';
import CrownIcon from '@assets/img/icons/crown-with-shadow.svg?react';
import { AdsContext } from '@components/Ads/context';
import {
  MainButton,
  WatchSecondaryButton,
  YellowButton,
} from '@components/ui/Button/Button';
import { useAppDispatch, useAppSelector } from '@redux/store';
import cn from 'classnames';
import { logEvent } from 'firebase/analytics';
import { AnimatePresence, motion } from 'framer-motion';
import { type LottieRefCurrentProps } from 'lottie-react';

import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { useIntroState } from '@/common/hooks/intro-ref';
import { entriesToSingleFieldObjArray } from '@/common/lib/array';
import { SendAnalitics } from '@/common/lib/playdeck';
import { Confetti } from '@/components/Animations/Confetti/Confetti';
import { StringMain } from '@/components/Typography/Typography';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { analytics } from '@/firebase';
import { setOpenChestIsActive } from '@/redux/reducers/modalsReducer';

import { MotionContainerAnimation } from './MotionContainerAnimation';
import { MotionRewards } from './MotionRewards';
import { MotionTitleAndDescription } from './MotionTitleAndDescription';

import css from './styles.module.scss';

const OpenChest: FC = () => {
  const { t } = useTranslation();
  const isOpen = useAppSelector((state) => state.modals.openChestIsActive);
  const statusVIP = useCheckVIPstatus();
  const { showAd, adsIsLoading } = useContext(AdsContext);
  const step27 = useIntroState(27);
  const [adsView, setAdsView] = useState(false);
  const chest = useAppSelector((state) => state.contract.chest);
  const [applyChest, { isLoading }] = useApplyChestMutation();
  const dispatch = useAppDispatch();
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const [isConfettiAnimationStart, setIsConfettiAnimationStart] =
    useState(false);
  const [containerRef, containerMeasure] = useMeasure();
  const stars = useAppSelector(
    (state) => state.contract.chestBoxRequiredPoints,
  );

  const onClick = (): void => {
    setIsConfettiAnimationStart(true);

    if (chest?.id) {
      applyChest({ id: chest.id, adsView })
        .unwrap()
        .then(() => {
          SendAnalitics.chestApply({
            stars,
            reward: Object.entries(chest.data).map(
              entriesToSingleFieldObjArray,
            ),
            use_up: adsView,
          });
          setIsConfettiAnimationStart(true);
        });
      Object.entries(chest.data).forEach(([name, value]) => {
        if (!analytics) return;
        logEvent(analytics, 'currency_earned', {
          currency_type: name,
          amount: value,
          source: 'open_box',
          box_id: chest.id,
          view_ads: false,
        });
      });
    }
  };

  const onAdsClick = (): void => {
    if (statusVIP) {
      setAdsView(true);
    } else {
      showAd({
        placements: 'chest',
        onReward: () => {
          setAdsView(true);
        },
        onError: () => {
          showToast(t('Ads launch error'));
        },
      });
    }
  };

  const checkMainButton: boolean = statusVIP ? adsView : true;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cn(css.openChest, step27.showAnimation && css.overlay)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            ease: 'easeOut',
            duration: 0.3,
          }}
        >
          {isConfettiAnimationStart && (
            <Confetti
              className="-z-[1] w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              lottieRef={lottieRef}
              onDOMLoaded={(event) => {
                console.log('dom loaded');
                console.log(event);
              }}
              onComplete={() => {
                setIsConfettiAnimationStart(false);
                setAdsView(false);
                dispatch(setOpenChestIsActive(false));
              }}
              loop={false}
              autoPlay={true}
              speed={2}
              width="100%"
              height="100%"
            />
          )}
          <motion.span
            initial={{ opacity: 0 }}
            animate={isConfettiAnimationStart ? { opacity: 0.5 } : undefined}
            transition={{ ease: 'easeOut', duration: 0.3 }}
            className={css.finalEffect}
          ></motion.span>
          <MotionTitleAndDescription />
          <MotionRewards
            adsView={adsView}
            containerMeasure={containerMeasure}
          />
          <MotionContainerAnimation
            ref={containerRef}
            rect={containerMeasure}
          />
          <motion.div
            initial={{ y: 48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              ease: 'easeOut',
              delay: 1.7,
              duration: 0.3,
            }}
            className="w-full flex gap-1 px-4 pb-10 relative z-[10]"
          >
            {!adsView && (
              <>
                {statusVIP ? (
                  <YellowButton
                    className="flex-1 gap-1"
                    isLoading={adsIsLoading}
                    onClick={onAdsClick}
                    disabled={adsIsLoading}
                    text=""
                  >
                    <CrownIcon className="w-6 h-6" />
                    <StringMain weight="extrabold" className="btn-text-shadow">
                      {t('Free + 20%')}
                    </StringMain>
                  </YellowButton>
                ) : (
                  <WatchSecondaryButton
                    className="flex-1"
                    isLoading={adsIsLoading}
                    onClick={onAdsClick}
                    additionText="+20%"
                    isVIP={statusVIP}
                    disabled={adsIsLoading}
                    text="ADS"
                  />
                )}
              </>
            )}
            {checkMainButton && (
              <MainButton
                onClick={onClick}
                isLoading={isLoading}
                disabled={isLoading}
                className={cn(
                  'flex-1 relative z-[100]',
                  step27.showAnimation && 'pointer-events-auto',
                )}
                text={t('Claim')}
              ></MainButton>
            )}
          </motion.div>
          {/* {isConfettiAnimationStart && (
            <Confetti
              className="-z-[1] w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
              lottieRef={lottieRef}
              onDOMLoaded={(event) => {
                console.log('dom loaded');
                console.log(event);
              }}
              onComplete={() => {
                setIsConfettiAnimationStart(false);
                setAdsView(false);
                dispatch(setOpenChestIsActive(false));
              }}
              loop={false}
              autoPlay={true}
              speed={2}
              width="100%"
              height="100%"
            />
          )}
          <Picture
            img={starsPng}
            webp={starsWebp}
            className="absolute bottom-0 left-0 w-full h-auto"
            alt="stars background"
          />
          <div className="w-full flex flex-col items-center px-4 pb-12">
            {isRewardAnimationStart && (
              <motion.div
                initial={{ scale: 0.7292, y: 30 }}
                animate={{ scale: 1, y: 0 }}
                transition={{
                  ease: cubicBezier(0.68, -0.55, 0.27, 1.55),
                  duration: 0.3,
                }}
                className="flex flex-col items-center"
              >
                <H1 weight="bold" className="mb-2">
                  {t('Congrats!')}
                </H1>
                <StringSmall weight="regular" className="!text-white/60 mb-6">
                  {t('You have opened a prestige container')}
                </StringSmall>
              </motion.div>
            )}
            <div className="w-full px-2 flex items-center gap-2 mb-4">
              {rewardList?.map(({ kind, value }, index) => {
                const label =
                  kind in text
                    ? text[kind]
                    : kind in resourceNames
                      ? resourceNames[kind as ResourceTypeText].nameMaterial
                      : kind in plantNames
                        ? plantNames[kind as PlantTypeText].nameMaterial
                        : kind;

                return (
                  isRewardAnimationStart && (
                    <motion.div
                      layoutId={`reward-${kind}-${value}`}
                      transition={{
                        layout: {
                          delay: index * 0.3 + 0.3,
                          ease: cubicBezier(0.68, -0.55, 0.27, 1.55),
                          duration: 0.3,
                        },
                      }}
                      className="flex-1"
                    >
                      <Reward
                        kind={kind}
                        value={toShortNumFormat(
                          adsView ? Math.round(value * 1.2) : value,
                        )}
                        label={label || kind}
                        imageSize={'4.5rem'}
                        className={cn(css.reward, adsView && css.bonus)}
                      />
                    </motion.div>
                  )
                );
              })}
            </div>
            <motion.div
              initial={{ scale: 0.775 }}
              animate={{ scale: 1 }}
              transition={{
                ease: cubicBezier(0.68, -0.55, 0.27, 1.55),
                duration: 0.3,
              }}
              onAnimationEnd={() => console.log('animation end')}
              onAnimationComplete={() => setIsRewardAnimationStart(true)}
              className="relative flex"
            >
              <LightAnimation
                isAnimationStart={isRewardAnimationStart}
                className="top-0"
              />
              <div
                style={{
                  width: yellowEffectSize(183),
                  height: yellowEffectSize(128),
                  top: yellowEffectSize(36),
                }}
                className={css.yellowEffect}
              ></div>
              <motion.div
                initial={{ scale: toScale(75), x: '-50%', y: '-50%' }}
                className="absolute h-[9.25rem] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 px-4 flex gap-[2px]"
              >
                {rewardList?.map(({ kind, value }, index) => {
                  const label =
                    kind in text
                      ? text[kind]
                      : kind in resourceNames
                        ? resourceNames[kind as ResourceTypeText].nameMaterial
                        : kind in plantNames
                          ? plantNames[kind as PlantTypeText].nameMaterial
                          : kind;

                  return (
                    <div className="min-w-[7.5rem] relative">
                      {!isRewardAnimationStart && (
                        <motion.div
                          transition={{
                            layout: {
                              delay: index * 0.3 + 0.3,
                              ease: cubicBezier(0.68, -0.55, 0.27, 1.55),
                              duration: 0.3,
                            },
                          }}
                          layoutId={`reward-${kind}-${value}`}
                        >
                          <Reward
                            kind={kind}
                            value={toShortNumFormat(
                              adsView ? Math.round(value * 1.2) : value,
                            )}
                            label={label || kind}
                            imageSize={'4.5rem'}
                            className={cn(css.reward, adsView && css.bonus)}
                          />
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </motion.div>
              <Picture
                className={cn('w-60 h-60 shrink-0 mb-4', css.dropShadow)}
                img={chestPng}
                webp={chestWebp}
                alt="chest"
              />
              <Picture
                className={css.shine}
                img={shinePng}
                webp={shineWebp}
                alt="shine"
              />
              <span className={css.gradient}></span>
            </motion.div>
            <motion.div
              initial={{ y: 48 }}
              animate={{ y: 0 }}
              transition={{
                ease: cubicBezier(0.68, -0.55, 0.27, 1.55),
                duration: 0.3,
              }}
              className="w-full flex gap-1"
            >
              {!adsView && (
                <WatchButton
                  className="flex-1"
                  isLoading={adsIsLoading}
                  onClick={onAdsClick}
                  additionText="+20%"
                  isVIP={statusVIP}
                  disabled={adsIsLoading}
                />
              )}
              {checkMainButton && (
                <MainButton
                  onClick={onClick}
                  isLoading={isLoading}
                  disabled={isLoading}
                  className={cn(
                    'flex-1 relative z-[100]',
                    step27.showAnimation && 'pointer-events-auto',
                  )}
                  text={t('Claim')}
                ></MainButton>
              )}
            </motion.div>
          </div> */}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { OpenChest };
